import {useTranslation} from "react-i18next";
import DefaultDialog, {DefaultDialogSize} from "../../../components/Dialog/ClosableDialog";
import {Button, FormControl, Stack} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import {useDeviceMassStorageDownloadMutation} from "../../../app/api/api";
import {DeviceJobField} from "../../../app/api/model/device";
import OrganizationStageSelect from "../../../components/input/OrganizationStageSelect";
import {useFormik} from "formik";
import {DeviceJobType} from "../../../app/api/model/deviceJob";
import {UseModal} from "../../../hooks/useModal";
import FormDatePicker from "../../../components/input/FormDatePicker";
import {getDeviceMassStorageValidationSchema} from "./validation";
import {getFormErrorText} from "../../../utils/getFormTextError";

interface DeviceMassStorageDownloadDialogProps {
    open: boolean;
    onClose: () => void;
    deviceId: number;
    modal: UseModal;
    organizationId?: number;
}

export default function DeviceMassStorageDownloadDialog({
    open,
    onClose,
    deviceId,
    modal,
    organizationId,
}: DeviceMassStorageDownloadDialogProps) {
    const {t} = useTranslation();
    const [deviceMassStorageDownload] = useDeviceMassStorageDownloadMutation();
    const form = useFormik({
        initialValues: {
            [DeviceJobField.STAGE]: "",
            [DeviceJobField.PERIOD_START]: "",
            [DeviceJobField.PERIOD_END]: "",
        },
        validationSchema: getDeviceMassStorageValidationSchema(t),
        onSubmit: (values) => {
            deviceMassStorageDownload({
                deviceId,
                [DeviceJobField.STAGE]: values[DeviceJobField.STAGE],
                [DeviceJobField.TYPE]: DeviceJobType.MASS_STORAGE,
                [DeviceJobField.PERIOD_START]: dayjs(values[DeviceJobField.PERIOD_START]).utc().format("YYYY-MM-DD"),
                [DeviceJobField.PERIOD_END]: dayjs(values[DeviceJobField.PERIOD_END]).utc().format("YYYY-MM-DD"),
            });
            handleClose();
        },
    });

    const handleClose = () => {
        form.resetForm({values: form.initialValues});
        onClose?.();
        modal.onClose();
    };

    return (
        <DefaultDialog
            size={DefaultDialogSize.MEDIUM}
            open={open}
            titleKey={"action_downloadMassStorage"}
            onClose={handleClose}
            actions={
                <Button onClick={form.submitForm} color="primary">
                    {t("action_download")}
                </Button>
            }
        >
            <Stack spacing={2} pt={1}>
                <OrganizationStageSelect
                    fieldName={DeviceJobField.STAGE}
                    label={t("organizations_stage")}
                    form={form}
                    t={t}
                    requestParams={{organizationId}}
                    hideEmptyOption={true}
                />
            </Stack>
            <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between"}}>
                <FormControl
                    margin={"dense"}
                    sx={{width: "100%", maxWidth: 200}}
                    size="small"
                    error={!!getFormErrorText(form, DeviceJobField.PERIOD_START)}
                >
                    <FormDatePicker
                        fieldName={DeviceJobField.PERIOD_START}
                        form={form}
                        label={t("deviceJob_periodStart")}
                        t={t}
                        disableFuture={true}
                    />
                </FormControl>
                <FormControl
                    margin={"dense"}
                    sx={{width: "100%", maxWidth: 200}}
                    size="small"
                    error={!!getFormErrorText(form, DeviceJobField.PERIOD_END)}
                >
                    <FormDatePicker
                        fieldName={DeviceJobField.PERIOD_END}
                        form={form}
                        label={t("deviceJob_periodEnd")}
                        t={t}
                        disableFuture={true}
                    />
                </FormControl>
            </div>
        </DefaultDialog>
    );
}
